import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  TextField,
  Autocomplete,
  Typography,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
  Link,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  getBillabilityTypes,
  getCoreRatioBasis,
  getCoreRatioBuckets,
  getCoreRatioMapping,
  getPrograms,
  createCoreRatioMapping,
  updateCoreRatioMapping,
  deleteCoreRatioMapping,
  bulkUpdateCoreRatioMappings,
  getCountries,
} from "services/userServices";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";

const CoreRatioTables = () => {
  // Custom font styles
  const fontStyles = {
    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
    letterSpacing: "0.10em",
    color: "#3F5765",
  };

  const headerFontStyles = {
    ...fontStyles,
    fontWeight: "bold",
  };

  // State Management
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showInactive, setShowInactive] = useState(false);
  const [highlightedCells, setHighlightedCells] = useState(new Set());

  // Sorting State
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRecord, setMenuRecord] = useState(null);
  const dispatch = useDispatch();

  const showSnack = (message, severity) => {
    const obj = {
      open: true,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmColor: "primary",
  });

  // Dropdown Options State
  const [programs, setPrograms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [coreRatioBuckets, setCoreRatioBuckets] = useState([]);
  const [billabilityTypes, setBillabilityTypes] = useState([]);
  const [basisTypes, setBasisTypes] = useState([]);

  // Validation State
  const [validationErrors, setValidationErrors] = useState({
    documentation: "",
    recentComments: "",
  });

  // Bulk Edit Header Values
  const [headerFilters, setHeaderFilters] = useState({
    program: null,
    startDate: null,
    endDate: null,
    country: null,
    coreRatioBucket: null,
    coreRatioTarget: "",
    billability: null,
    basis: null,
    documentation: "",
    recentComments: "",
  });

  // Form State
  const [formData, setFormData] = useState({
    coreRatioBillabilityMapId: null,
    coreRatioCountryJobCodeMapId: null,
    program: null,
    programCode: "",
    startDate: null,
    endDate: null,
    country: null,
    countryCode: "",
    jobCode: "",
    departmentCode: "",
    coreRatioBucket: null,
    coreRatioTarget: "",
    billability: null,
    basis: null,
    documentation: "",
    recentComments: "",
  });

  // Load Initial Data
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await loadInitialData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load initial data:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  // Reload data when showInactive changes
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await loadTableData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load table data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [showInactive]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTableData(), loadDropdownOptions()]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      showSnackbar("Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };

  // API Calls
  // Helper function to transform single item
  const transformDataItem = (item) => ({
    coreRatioBillabilityMapId: item.CoreRatioBillabilityMapId,
    coreRatioCountryJobCodeMapId: item.CoreRatioCountryJobCodeMapId,
    startDate: item.StartDate ? new Date(item.StartDate) : null,
    endDate: item.EndDate ? new Date(item.EndDate) : null,
    program: {
      name: item.ProgramName,
      code: item.ProgramCode,
    },
    country: {
      name: item.CountryName || item.CountryCode,
      code: item.CountryCode,
    },
    coreRatioBucket: {
      id: item.CoreRatioBucketId,
      name: item.CoreRatioBucketName,
    },
    coreRatioTarget: item.CoreRatioTarget,
    billability: {
      id: item.CoreRatioBillabilityTypeId,
      name: item.CoreRatioBillabilityTypeName,
    },
    basis:
      item.CoreRatioBasisId && item.CoreRatioBasisName
        ? {
            id: item.CoreRatioBasisId,
            name: item.CoreRatioBasisName,
          }
        : null,
    jobCode: item.JobCode,
    departmentCode: item.DepartmentCode,
    documentation: item.Documentation || "",
    recentComments: item.RecentComments || "",
  });

  // Helper function to handle successful response
  const handleSuccessfulResponse = (data, setTableData, showSnackbar) => {
    if (Array.isArray(data)) {
      const transformedData = data.map(transformDataItem);
      setTableData(transformedData);
    } else {
      console.warn("API returned data but it is not an array:", data);
      setTableData([]);
      showSnackbar("No data available", "info");
    }
  };

  // Helper function to handle failed response
  const handleFailedResponse = (response, setTableData, showSnackbar) => {
    console.warn("API returned no data or status is false");
    setTableData([]);
    showSnackbar(response.data.message || "No data available", "info");
  };

  // Refactored main function
  const loadTableData = async () => {
    setLoading(true);
    try {
      const response = await getCoreRatioMapping(
        null,
        null,
        0,
        showInactive,
        null,
      );

      if (response.data.status && response.data.data) {
        handleSuccessfulResponse(
          response.data.data,
          setTableData,
          showSnackbar,
        );
      } else {
        handleFailedResponse(response, setTableData, showSnackbar);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setTableData([]);
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to process programs response
  const processProgramsResponse = (response, setPrograms) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setPrograms(
        response.data.data.map((p) => ({
          name: p.ProgramName,
          code: p.ProgramCode,
        })),
      );
    } else {
      setPrograms([]);
    }
  };

  // Helper function to process countries response
  const processCountriesResponse = (response, setCountries) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setCountries(
        response.data.data.map((c) => ({
          name: c.CountryName,
          code: c.CountryCode,
        })),
      );
    } else {
      setCountries([]);
    }
  };

  // Helper function to process buckets response
  const processBucketsResponse = (response, setCoreRatioBuckets) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setCoreRatioBuckets(
        response.data.data.map((b) => ({
          id: b.CoreRatioBucketId,
          name: b.CoreRatioBucketName,
        })),
      );
    } else {
      setCoreRatioBuckets([]);
    }
  };

  // Helper function to process billability response
  const processBillabilityResponse = (response, setBillabilityTypes) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setBillabilityTypes(
        response.data.data.map((bt) => ({
          id: bt.CoreRatioBillabilityTypeId,
          name: bt.CoreRatioBillabilityTypeName,
        })),
      );
    } else {
      setBillabilityTypes([]);
    }
  };

  // Helper function to process basis response
  const processBasisResponse = (response, setBasisTypes) => {
    if (response.data.status && Array.isArray(response.data.data)) {
      setBasisTypes(
        response.data.data.map((b) => ({
          id: b.CoreRatioBasisId,
          name: b.CoreRatioBasisName,
        })),
      );
    } else {
      setBasisTypes([]);
    }
  };

  // Refactored main function
  const loadDropdownOptions = async () => {
    try {
      const [programsRes, countriesRes, bucketsRes, billabilityRes, basisRes] =
        await Promise.all([
          getPrograms(),
          getCountries(),
          getCoreRatioBuckets(),
          getBillabilityTypes(),
          getCoreRatioBasis(),
        ]);

      processProgramsResponse(programsRes, setPrograms);
      processCountriesResponse(countriesRes, setCountries);
      processBucketsResponse(bucketsRes, setCoreRatioBuckets);
      processBillabilityResponse(billabilityRes, setBillabilityTypes);
      processBasisResponse(basisRes, setBasisTypes);
    } catch (error) {
      console.error("Error loading dropdown options:", error);
      showSnackbar("Failed to load dropdown options", "error");
    }
  };

  // Utility Functions
  const showSnackbar = (message, severity = "success") => {
    showSnack(message, severity);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const day = date.getDate();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatDateForSQL = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDates = (startDate, endDate) => {
    if (startDate && endDate) {
      if (endDate < startDate) {
        return "End date cannot be earlier than start date";
      }
    }
    return null;
  };

  // Validation Functions
  const validateDocumentation = (value) => {
    if (value && value.length > 300) {
      return "Documentation cannot exceed 300 characters";
    }
    return "";
  };

  const validateRecentComments = (value) => {
    if (value && value.length > 2000) {
      return "Recent Comments cannot exceed 2000 characters";
    }
    return "";
  };

  const validateForm = () => {
    const docError = validateDocumentation(formData.documentation);
    const commentsError = validateRecentComments(formData.recentComments);

    setValidationErrors({
      documentation: docError,
      recentComments: commentsError,
    });

    return !docError && !commentsError;
  };

  // Helper function to check if text is a URL
  const isURL = (text) => {
    if (!text) return false;
    const urlPattern = /^(https?:\/\/)|(www\.)/i;
    return urlPattern.test(text.trim());
  };

  // Helper function to ensure URL has protocol
  const ensureProtocol = (url) => {
    if (!url) return url;
    const trimmedUrl = url.trim();
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      return `https://${trimmedUrl}`;
    }
    return trimmedUrl;
  };

  // Helper function to render documentation content
  const renderDocumentation = (text) => {
    if (!text) return "";

    if (isURL(text)) {
      return (
        <Link
          href={ensureProtocol(text)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            ...fontStyles,
            color: "primary.main",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {text}
        </Link>
      );
    }

    return text;
  };

  // Helper function to create cell key for highlighting
  const getCellKey = (rowId, field) => `${rowId}-${field}`;

  // Helper function to check if cell is highlighted
  const isCellHighlighted = (rowId, field) => {
    return highlightedCells.has(getCellKey(rowId, field));
  };

  // Confirmation Dialog Handlers
  const showConfirmDialog = (
    title,
    message,
    onConfirm,
    confirmColor = "primary",
  ) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmColor,
    });
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      confirmColor: "primary",
    });
  };

  const handleConfirmDialogAccept = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    handleConfirmDialogClose();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = tableData.map((n) => n.coreRatioBillabilityMapId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Row click handler
  const handleRowClick = (event, id) => {
    const target = event.target;
    const isInteractive =
      target.closest("button") ||
      target.closest("a") ||
      target.closest('input[type="checkbox"]') ||
      target.closest(".MuiAutocomplete-root") ||
      target.closest(".MuiTextField-root");

    if (!isInteractive) {
      handleSelectRow(id);
    }
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sorting Handlers
  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const getSortValue = (row, column) => {
    switch (column) {
      case "program":
        return row.program?.name?.toLowerCase() || "";
      case "programCode":
        return row.program?.code?.toLowerCase() || "";
      case "startDate":
        return row.startDate?.getTime() || 0;
      case "endDate":
        return row.endDate?.getTime() || 0;
      case "country":
        return row.country?.name?.toLowerCase() || "";
      case "countryCode":
        return row.country?.code?.toLowerCase() || "";
      case "coreRatioBucket":
        return row.coreRatioBucket?.name?.toLowerCase() || "";
      case "coreRatioTarget":
        return row.coreRatioTarget || 0;
      case "billability":
        return row.billability?.name?.toLowerCase() || "";
      case "basis":
        return row.basis?.name?.toLowerCase() || "";
      case "documentation":
        return row.documentation?.toLowerCase() || "";
      case "recentComments":
        return row.recentComments?.toLowerCase() || "";
      default:
        return "";
    }
  };

  const getSortedData = () => {
    if (!orderBy) return tableData;

    return [...tableData].sort((a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const paginatedData = getSortedData().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Bulk Edit Functions
  // Helper function to validate selection
  const validateSelection = (
    selected,
    showSnackbar,
    setHeaderFilters,
    field,
  ) => {
    if (selected.length === 0) {
      showSnackbar("Please select rows to apply bulk edit", "warning");
      const resetValue =
        field === "coreRatioTarget" ||
        field === "documentation" ||
        field === "recentComments"
          ? ""
          : null;
      setHeaderFilters((prev) => ({ ...prev, [field]: resetValue }));
      return false;
    }
    return true;
  };

  // Helper function to validate character limits
  const validateCharacterLimits = (
    field,
    value,
    showSnackbar,
    setHeaderFilters,
  ) => {
    if (field === "documentation" && value.length > 300) {
      showSnackbar("Documentation cannot exceed 300 characters", "error");
      setHeaderFilters((prev) => ({ ...prev, documentation: "" }));
      return false;
    }

    if (field === "recentComments" && value.length > 2000) {
      showSnackbar("Recent Comments cannot exceed 2000 characters", "error");
      setHeaderFilters((prev) => ({ ...prev, recentComments: "" }));
      return false;
    }

    return true;
  };

  // Helper function to validate dates
  const validateDateFields = (
    field,
    value,
    selected,
    tableData,
    showSnackbar,
    setHeaderFilters,
    validateDates,
  ) => {
    if (field !== "startDate" && field !== "endDate") {
      return true;
    }

    const selectedRows = tableData.filter((row) =>
      selected.includes(row.coreRatioBillabilityMapId),
    );

    for (const row of selectedRows) {
      const startDate = field === "startDate" ? value : row.startDate;
      const endDate = field === "endDate" ? value : row.endDate;

      const dateError = validateDates(startDate, endDate);
      if (dateError) {
        showSnackbar(
          `${dateError} for ${row.program?.name} (${row.country?.name})`,
          "error",
        );
        setHeaderFilters((prev) => ({ ...prev, [field]: null }));
        return false;
      }
    }

    return true;
  };
  // Helper function to get update value
  const getUpdateValue = (field, value, formatDateForSQL) => {
    if (field === "startDate" || field === "endDate") {
      return formatDateForSQL(value);
    }

    // Handle text fields
    if (field === "documentation" || field === "recentComments") {
      return value || "";
    }

    // Handle number fields
    if (field === "coreRatioTarget") {
      return value?.toString() || "";
    }

    // Handle object fields with safe access
    const fieldValueMap = {
      program: value?.code || "",
      country: value?.code || "",
      coreRatioBucket: value?.id?.toString() || "",
      billability: value?.id?.toString() || "",
      basis: value?.id?.toString() || "",
    };

    const mappedValue = fieldValueMap[field];
    const finalValue = mappedValue !== undefined ? mappedValue : value;

    return finalValue;
  };

  // Helper function to perform bulk update
  const performBulkUpdate = async (
    field,
    value,
    selected,
    bulkUpdateCoreRatioMappings,
    formatDateForSQL,
  ) => {
    console.log("🚀 performBulkUpdate called with:", {
      field,
      value,
      valueType: typeof value,
      selectedCount: selected?.length,
      selected,
    });

    const fieldMapping = {
      program: "ProgramCode",
      country: "CountryCode",
      coreRatioBucket: "CoreRatioBucketId",
      coreRatioTarget: "CoreRatioTarget",
      billability: "CoreRatioBillabilityTypeId",
      basis: "CoreRatioBasisId",
      startDate: "StartDate",
      endDate: "EndDate",
      documentation: "Documentation",
      recentComments: "Comment",
    };

    console.log("🗺️ Field mapping:", {
      field,
      mappedField: fieldMapping[field],
    });

    const updateValue = getUpdateValue(field, value, formatDateForSQL);

    console.log("💾 Update value obtained:", updateValue);

    const bulkUpdatePayload = {
      mappingIds: selected.join(","),
      updateField: fieldMapping[field],
      updateValue: updateValue,
    };

    console.log("📦 Bulk update payload:", bulkUpdatePayload);

    try {
      const result = await bulkUpdateCoreRatioMappings(bulkUpdatePayload);
      console.log("✅ Bulk update successful:", result);
      return result;
    } catch (error) {
      console.error("❌ Bulk update failed:", error);
      throw error;
    }
  };

  // Helper function to handle successful update
  const handleSuccessfulUpdate = (
    selected,
    field,
    showSnackbar,
    setHighlightedCells,
    getCellKey,
    loadTableData,
    setSelected,
    setHeaderFilters,
  ) => {
    showSnackbar(`Updated ${selected.length} rows`, "success");

    const newHighlightedCells = new Set();
    selected.forEach((rowId) => {
      newHighlightedCells.add(getCellKey(rowId, field));
    });
    setHighlightedCells(newHighlightedCells);

    setTimeout(() => {
      setHighlightedCells(new Set());
    }, 3000);

    loadTableData();
    setSelected([]);

    setHeaderFilters({
      program: null,
      startDate: null,
      endDate: null,
      country: null,
      coreRatioBucket: null,
      coreRatioTarget: "",
      billability: null,
      basis: null,
      documentation: "",
      recentComments: "",
    });
  };

  // Refactored main function
  const applyBulkEdit = async (field, value) => {
    if (!validateSelection(selected, showSnackbar, setHeaderFilters, field)) {
      return;
    }

    if (
      !validateCharacterLimits(field, value, showSnackbar, setHeaderFilters)
    ) {
      return;
    }

    if (
      !validateDateFields(
        field,
        value,
        selected,
        tableData,
        showSnackbar,
        setHeaderFilters,
        validateDates,
      )
    ) {
      return;
    }

    showConfirmDialog(
      "Confirm Bulk Update",
      `Are you sure you want to update ${selected.length} selected row(s)?`,
      async () => {
        try {
          const response = await performBulkUpdate(
            field,
            value,
            selected,
            bulkUpdateCoreRatioMappings,
            formatDateForSQL,
          );

          if (response.data.status) {
            handleSuccessfulUpdate(
              selected,
              field,
              showSnackbar,
              setHighlightedCells,
              getCellKey,
              loadTableData,
              setSelected,
              setHeaderFilters,
            );
          } else {
            showSnackbar(
              response.data.message || "Failed to update records",
              "error",
            );
          }
        } catch (error) {
          console.error("Error in bulk update:", error);
          showSnackbar("Failed to update records", "error");
        }
      },
      "primary",
    );
  };

  // Bulk Delete Function
  const handleBulkDelete = () => {
    if (selected.length === 0) {
      showSnackbar("Please select rows to delete", "warning");
      return;
    }

    const selectedRows = tableData.filter((row) =>
      selected.includes(row.coreRatioBillabilityMapId),
    );

    const message = (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete {selected.length} selected record(s)?
        </Typography>
        <Typography variant="body2" sx={{ ...fontStyles, fontWeight: "bold" }}>
          Records to be deleted:
        </Typography>
        <Box sx={{ maxHeight: "200px", overflow: "auto", mt: 1 }}>
          {selectedRows.slice(0, 10).map((row, index) => (
            <Typography key={index} variant="body2" sx={fontStyles}>
              • {row.program?.name} ({row.program?.code}) - {row.country?.name}{" "}
              - {row.coreRatioBucket?.name}
            </Typography>
          ))}
          {selectedRows.length > 10 && (
            <Typography variant="body2" sx={{ ...fontStyles, mt: 1 }}>
              ... and {selectedRows.length - 10} more
            </Typography>
          )}
        </Box>
      </Box>
    );

    showConfirmDialog(
      "Confirm Bulk Delete",
      message,
      async () => {
        try {
          setLoading(true);
          const deletePromises = selected.map((id) => {
            const deletePayload = {
              coreRatioBillabilityMapId: id,
              hardDelete: false,
              deleteDate: null,
            };
            return deleteCoreRatioMapping(deletePayload);
          });

          const responses = await Promise.all(deletePromises);

          const successCount = responses.filter(
            (response) => response.data.status,
          ).length;
          const failCount = responses.length - successCount;

          if (successCount > 0) {
            showSnackbar(
              `Successfully deleted ${successCount} record(s)${
                failCount > 0 ? `, ${failCount} failed` : ""
              }`,
              failCount > 0 ? "warning" : "success",
            );
            await loadTableData();
            setSelected([]);
          } else {
            showSnackbar("Failed to delete records", "error");
          }
        } catch (error) {
          console.error("Error in bulk delete:", error);
          showSnackbar("Failed to delete records", "error");
        } finally {
          setLoading(false);
        }
      },
      "error",
    );
  };

  // CRUD Operations
  const handleAdd = () => {
    setEditMode(false);
    setFormData({
      coreRatioBillabilityMapId: null,
      coreRatioCountryJobCodeMapId: null,
      program: null,
      programCode: "",
      startDate: new Date(),
      endDate: null,
      country: null,
      countryCode: "",
      jobCode: "",
      departmentCode: "",
      coreRatioBucket: null,
      coreRatioTarget: "",
      billability: null,
      basis: null,
      documentation: "",
      recentComments: "",
    });
    setValidationErrors({ documentation: "", recentComments: "" });
    setOpenDialog(true);
  };

  const handleEdit = (rowData) => {
    setEditMode(true);
    setFormData({
      coreRatioBillabilityMapId: rowData.coreRatioBillabilityMapId,
      coreRatioCountryJobCodeMapId: rowData.coreRatioCountryJobCodeMapId,
      program: rowData.program,
      programCode: rowData.program?.code || "",
      startDate: rowData.startDate,
      endDate: rowData.endDate,
      country: rowData.country,
      countryCode: rowData.country?.code || "",
      jobCode: rowData.jobCode || "",
      departmentCode: rowData.departmentCode || "",
      coreRatioBucket: rowData.coreRatioBucket,
      coreRatioTarget: rowData.coreRatioTarget,
      billability: rowData.billability,
      basis: rowData.basis,
      documentation: rowData.documentation,
      recentComments: rowData.recentComments,
    });
    setValidationErrors({ documentation: "", recentComments: "" });
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleDelete = async (rowData) => {
    const message = (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete this record?
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Program:</strong> {rowData.program?.name} (
          {rowData.program?.code})
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Country:</strong> {rowData.country?.name}
        </Typography>
        <Typography variant="body2" sx={fontStyles}>
          <strong>Bucket:</strong> {rowData.coreRatioBucket?.name}
        </Typography>
      </Box>
    );

    showConfirmDialog(
      "Confirm Delete",
      message,
      async () => {
        try {
          const deletePayload = {
            coreRatioBillabilityMapId: rowData.coreRatioBillabilityMapId,
            hardDelete: false,
            deleteDate: null,
          };

          const response = await deleteCoreRatioMapping(deletePayload);

          if (response.data.status) {
            showSnackbar("Record deleted successfully", "success");
            await loadTableData();
          } else {
            showSnackbar(
              response.data.message || "Failed to delete record",
              "error",
            );
          }
        } catch (error) {
          console.error("Error deleting record:", error);
          showSnackbar("Failed to delete record", "error");
        }
      },
      "error",
    );
    handleMenuClose();
  };

  // Helper function to validate required fields
  const validateRequiredFields = (formData, showSnackbar) => {
    if (
      !formData.program ||
      !formData.country ||
      !formData.coreRatioBucket ||
      !formData.billability ||
      !formData.coreRatioTarget
    ) {
      showSnackbar("Please fill all required fields", "warning");
      return false;
    }
    return true;
  };

  // Helper function to validate form
  const validateFormData = (
    validateForm,
    validateDates,
    formData,
    showSnackbar,
  ) => {
    if (!validateForm()) {
      showSnackbar("Please fix validation errors before saving", "error");
      return false;
    }

    const dateError = validateDates(formData.startDate, formData.endDate);
    if (dateError) {
      showSnackbar(dateError, "error");
      return false;
    }

    return true;
  };

  // Helper function to create update payload
  const createUpdatePayload = (formData, formatDateForSQL) => {
    const payload = {
      coreRatioBillabilityMapId: formData.coreRatioBillabilityMapId,
      programCode: formData.program?.code,
      countryCode: formData.country?.code,
      jobCode: formData.jobCode || null,
      departmentCode: formData.departmentCode || null,
      coreRatioBucketId: formData.coreRatioBucket?.id,
      coreRatioTarget: formData.coreRatioTarget
        ? parseInt(formData.coreRatioTarget)
        : null,
      coreRatioBillabilityTypeId: formData.billability?.id || null,
      coreRatioBasisId: formData.basis?.id || null,
      documentation: formData.documentation || "",
      comment: formData.recentComments || "",
    };

    const formattedStartDate = formatDateForSQL(formData.startDate);
    const formattedEndDate = formatDateForSQL(formData.endDate);

    if (formattedStartDate) {
      payload.startDate = formattedStartDate;
    }

    if (formattedEndDate) {
      payload.endDate = formattedEndDate;
    }

    return payload;
  };

  // Helper function to create new record payload
  const createNewPayload = (formData, formatDateForSQL) => {
    const payload = {
      programCode: formData.program?.code,
      countryCode: formData.country?.code,
      coreRatioBucketId: formData.coreRatioBucket?.id,
      coreRatioTarget: parseInt(formData.coreRatioTarget),
      coreRatioBillabilityTypeId: formData.billability.id,
      documentation: formData.documentation || "",
      comment: formData.recentComments || "",
    };

    if (formData.jobCode) {
      payload.jobCode = formData.jobCode;
    }

    if (formData.departmentCode) {
      payload.departmentCode = formData.departmentCode;
    }

    if (formData.basis?.id) {
      payload.coreRatioBasisId = formData.basis.id;
    }

    const formattedStartDate = formatDateForSQL(formData.startDate);
    const formattedEndDate = formatDateForSQL(formData.endDate);

    if (formattedStartDate) {
      payload.startDate = formattedStartDate;
    }

    if (formattedEndDate) {
      payload.endDate = formattedEndDate;
    }

    return payload;
  };

  // Helper function to handle successful save
  const handleSuccessfulSave = async (
    message,
    showSnackbar,
    loadTableData,
    setOpenDialog,
    setValidationErrors,
  ) => {
    showSnackbar(message, "success");
    await loadTableData();
    setOpenDialog(false);
    setValidationErrors({ documentation: "", recentComments: "" });
  };

  // Helper function to handle failed save
  const handleFailedSave = (response, showSnackbar) => {
    showSnackbar(response.data.message || "Failed to save record", "error");
  };

  // Helper function to update existing record
  const updateExistingRecord = async (
    formData,
    formatDateForSQL,
    updateCoreRatioMapping,
    showSnackbar,
    loadTableData,
    setOpenDialog,
    setValidationErrors,
  ) => {
    const updatePayload = createUpdatePayload(formData, formatDateForSQL);
    const response = await updateCoreRatioMapping(updatePayload);

    if (response.data.status) {
      await handleSuccessfulSave(
        "Record updated successfully",
        showSnackbar,
        loadTableData,
        setOpenDialog,
        setValidationErrors,
      );
    } else {
      handleFailedSave(response, showSnackbar);
    }
  };

  // Helper function to create new record
  const createNewRecord = async (
    formData,
    formatDateForSQL,
    createCoreRatioMapping,
    showSnackbar,
    loadTableData,
    setOpenDialog,
    setValidationErrors,
  ) => {
    const createPayload = createNewPayload(formData, formatDateForSQL);
    const response = await createCoreRatioMapping(createPayload);

    if (response.data.status) {
      await handleSuccessfulSave(
        "Record created successfully",
        showSnackbar,
        loadTableData,
        setOpenDialog,
        setValidationErrors,
      );
    } else {
      handleFailedSave(response, showSnackbar);
    }
  };

  // Refactored main function
  const handleSave = async () => {
    if (!validateRequiredFields(formData, showSnackbar)) {
      return;
    }

    if (
      !validateFormData(validateForm, validateDates, formData, showSnackbar)
    ) {
      return;
    }

    try {
      if (editMode) {
        await updateExistingRecord(
          formData,
          formatDateForSQL,
          updateCoreRatioMapping,
          showSnackbar,
          loadTableData,
          setOpenDialog,
          setValidationErrors,
        );
      } else {
        await createNewRecord(
          formData,
          formatDateForSQL,
          createCoreRatioMapping,
          showSnackbar,
          loadTableData,
          setOpenDialog,
          setValidationErrors,
        );
      }
    } catch (error) {
      console.error("Error saving record:", error);
      showSnackbar("Failed to save record", "error");
    }
  };

  // Menu Handlers
  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setMenuRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRecord(null);
  };

  const handleToggleInactive = (event) => {
    setShowInactive(event.target.checked);
    setSelected([]);
    setPage(0);
  };

  const getHighlightedCellStyles = (rowId, field) => {
    if (isCellHighlighted(rowId, field)) {
      return {
        ...fontStyles,
        backgroundColor: "#fff4e6",
        transition: "background-color 0.3s ease",
      };
    }
    return fontStyles;
  };

  // Extract the paginatedData mapping logic
  const renderTableRow = (row) => {
    const isItemSelected = isSelected(row.coreRatioBillabilityMapId);

    return (
      <TableRow
        key={row.coreRatioBillabilityMapId}
        hover
        selected={isItemSelected}
        onClick={(event) =>
          handleRowClick(event, row.coreRatioBillabilityMapId)
        }
        sx={{
          cursor: "pointer",
          "&.Mui-selected": {
            "& .MuiTableCell-root": {
              color: fontStyles.color,
            },
          },
          "&.Mui-selected:hover": {
            "& .MuiTableCell-root": {
              color: fontStyles.color,
            },
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isItemSelected}
            onChange={() => handleSelectRow(row.coreRatioBillabilityMapId)}
          />
        </TableCell>
        <TableCell align="center">
          <Tooltip title="More actions">
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "program",
          )}
        >
          {row.program ? `${row.program.name} (${row.program.code})` : ""}
        </TableCell>
        <TableCell sx={fontStyles}>{row.program?.code}</TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "startDate",
          )}
        >
          {formatDate(row.startDate)}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "endDate",
          )}
        >
          {formatDate(row.endDate)}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "country",
          )}
        >
          {row.country?.name}
        </TableCell>
        <TableCell sx={fontStyles}>{row.country?.code}</TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "coreRatioBucket",
          )}
        >
          {row.coreRatioBucket?.name}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "coreRatioTarget",
          )}
        >
          {row.coreRatioTarget}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "billability",
          )}
        >
          {row.billability?.name}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(row.coreRatioBillabilityMapId, "basis")}
        >
          {row.basis?.name}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "documentation",
          )}
        >
          {renderDocumentation(row.documentation)}
        </TableCell>
        <TableCell
          sx={getHighlightedCellStyles(
            row.coreRatioBillabilityMapId,
            "recentComments",
          )}
        >
          {row.recentComments}
        </TableCell>
      </TableRow>
    );
  };

  // Extract table body rendering logic
  const renderTableBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={14} align="center" sx={fontStyles}>
            Loading...
          </TableCell>
        </TableRow>
      );
    }

    if (paginatedData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={14} align="center" sx={fontStyles}>
            No data available
          </TableCell>
        </TableRow>
      );
    }

    return paginatedData.map((row) => renderTableRow(row));
  };

  return (
    <>
      {loading && <BackdropComponent />}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  ...headerFontStyles,
                  textTransform: "none",
                  color: "#FFFFFF",
                }}
              >
                Add New
              </Button>

              {selected.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDelete}
                  sx={{
                    ...headerFontStyles,
                    textTransform: "none",
                    color: "#FFFFFF",
                  }}
                >
                  Delete Selected
                </Button>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={handleToggleInactive}
                  color="primary"
                />
              }
              label="Show Inactive Records"
              sx={{
                ...fontStyles,
                "& .MuiFormControlLabel-label": fontStyles,
              }}
            />
          </Box>

          {selected.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={`${selected.length} row(s) selected`}
                color="primary"
                onDelete={() => setSelected([])}
                sx={{
                  ...fontStyles,
                  color: "#FFFFFF",
                  "& .MuiChip-label": {
                    color: "#FFFFFF",
                  },
                }}
              />
            </Box>
          )}

          <TableContainer
            sx={{
              maxHeight: `calc(100vh - ${
                selected.length > 0 ? "340px" : "290px"
              })`,
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headerFontStyles}>
                    <Checkbox
                      indeterminate={
                        selected.length > 0 &&
                        selected.length < tableData.length
                      }
                      checked={
                        tableData.length > 0 &&
                        selected.length === tableData.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={headerFontStyles}
                    style={{ minWidth: "100px" }}
                  >
                    Actions
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "250px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "program"}
                      direction={orderBy === "program" ? order : "asc"}
                      onClick={() => handleSort("program")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Program
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "130px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "programCode"}
                      direction={orderBy === "programCode" ? order : "asc"}
                      onClick={() => handleSort("programCode")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Program Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "180px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "startDate"}
                      direction={orderBy === "startDate" ? order : "asc"}
                      onClick={() => handleSort("startDate")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Start Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "180px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "endDate"}
                      direction={orderBy === "endDate" ? order : "asc"}
                      onClick={() => handleSort("endDate")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      End Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "220px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "country"}
                      direction={orderBy === "country" ? order : "asc"}
                      onClick={() => handleSort("country")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Country
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "130px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "countryCode"}
                      direction={orderBy === "countryCode" ? order : "asc"}
                      onClick={() => handleSort("countryCode")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Country Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "220px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "coreRatioBucket"}
                      direction={orderBy === "coreRatioBucket" ? order : "asc"}
                      onClick={() => handleSort("coreRatioBucket")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Core Ratio Bucket
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "220px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "coreRatioTarget"}
                      direction={orderBy === "coreRatioTarget" ? order : "asc"}
                      onClick={() => handleSort("coreRatioTarget")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Core Ratio Target
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "200px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "billability"}
                      direction={orderBy === "billability" ? order : "asc"}
                      onClick={() => handleSort("billability")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Billability
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "180px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "basis"}
                      direction={orderBy === "basis" ? order : "asc"}
                      onClick={() => handleSort("basis")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Basis
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "300px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "documentation"}
                      direction={orderBy === "documentation" ? order : "asc"}
                      onClick={() => handleSort("documentation")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Documentation
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={headerFontStyles}
                    style={{ minWidth: "300px" }}
                  >
                    <TableSortLabel
                      active={orderBy === "recentComments"}
                      direction={orderBy === "recentComments" ? order : "asc"}
                      onClick={() => handleSort("recentComments")}
                      sx={{
                        ...headerFontStyles,
                        "& .MuiTableSortLabel-icon": {
                          color: `${headerFontStyles.color} !important`,
                        },
                      }}
                    >
                      Recent Comments
                    </TableSortLabel>
                  </TableCell>
                </TableRow>

                {/* Bulk Edit Row */}
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell padding="checkbox" />
                  <TableCell />
                  <TableCell style={{ minWidth: "250px" }}>
                    <Autocomplete
                      size="small"
                      options={programs}
                      fullWidth
                      getOptionLabel={(option) =>
                        option ? `${option.name} (${option.code})` : ""
                      }
                      value={headerFilters.program}
                      onChange={(e, value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          program: value,
                        });
                        if (value && value.code)
                          applyBulkEdit("program", value); // ✅ Check both value and value.code
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Bulk Edit Program"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "130px" }} />
                  <TableCell style={{ minWidth: "180px" }}>
                    <DatePicker
                      value={headerFilters.startDate}
                      onChange={(value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          startDate: value,
                        });
                        if (value) applyBulkEdit("startDate", value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Bulk Edit Start Date"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "180px" }}>
                    <DatePicker
                      value={headerFilters.endDate}
                      onChange={(value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          endDate: value,
                        });
                        if (value) applyBulkEdit("endDate", value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Bulk Edit End Date"
                          fullWidth
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "220px" }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={countries}
                      getOptionLabel={(option) => option.name || ""}
                      value={headerFilters.country}
                      onChange={(e, value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          country: value,
                        });
                        if (value && value.code)
                          applyBulkEdit("country", value); // ✅ Check both value and value.code
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Bulk Edit Country"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "130px" }} />
                  <TableCell style={{ minWidth: "220px" }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={coreRatioBuckets}
                      getOptionLabel={(option) => option.name || ""}
                      value={headerFilters.coreRatioBucket}
                      onChange={(e, value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          coreRatioBucket: value,
                        });
                        if (value && value.id)
                          applyBulkEdit("coreRatioBucket", value); // ✅ Check both value and value.id
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Bulk Edit Core Ratio Bucket"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "220px" }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Bulk Edit Core Ratio Target"
                      fullWidth
                      value={headerFilters.coreRatioTarget}
                      onChange={(e) =>
                        setHeaderFilters({
                          ...headerFilters,
                          coreRatioTarget: e.target.value,
                        })
                      }
                      onBlur={() =>
                        headerFilters.coreRatioTarget &&
                        applyBulkEdit(
                          "coreRatioTarget",
                          parseInt(headerFilters.coreRatioTarget),
                        )
                      }
                      inputProps={{ min: 0, max: 100 }}
                      sx={{
                        "& .MuiInputBase-input": fontStyles,
                        "& .MuiInputLabel-root": fontStyles,
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "200px" }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={billabilityTypes}
                      getOptionLabel={(option) => option.name || ""}
                      value={headerFilters.billability}
                      onChange={(e, value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          billability: value,
                        });
                        if (value && value.id)
                          applyBulkEdit("billability", value); // ✅ Check both value and value.id
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Bulk Edit Billability"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "180px" }}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={basisTypes}
                      getOptionLabel={(option) => option.name || ""}
                      value={headerFilters.basis}
                      onChange={(e, value) => {
                        setHeaderFilters({
                          ...headerFilters,
                          basis: value,
                        });
                        if (value && value.id) applyBulkEdit("basis", value); // ✅ Check both value and value.id
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Bulk Edit Basis"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": fontStyles,
                            "& .MuiInputLabel-root": fontStyles,
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell style={{ minWidth: "300px" }}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        size="small"
                        placeholder="Bulk Edit Documentation"
                        fullWidth
                        value={headerFilters.documentation}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue.length <= 300) {
                            setHeaderFilters({
                              ...headerFilters,
                              documentation: newValue,
                            });
                          }
                        }}
                        onBlur={() =>
                          headerFilters.documentation &&
                          applyBulkEdit(
                            "documentation",
                            headerFilters.documentation,
                          )
                        }
                        inputProps={{ maxLength: 300 }}
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          ...fontStyles,
                          fontSize: "0.65rem",
                          position: "absolute",
                          right: 8,
                          bottom: -18,
                          color: "#666",
                        }}
                      >
                        {headerFilters.documentation.length}/300
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ minWidth: "300px" }}>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        size="small"
                        placeholder="Bulk Edit Recent Comments"
                        fullWidth
                        value={headerFilters.recentComments}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue.length <= 2000) {
                            setHeaderFilters({
                              ...headerFilters,
                              recentComments: newValue,
                            });
                          }
                        }}
                        onBlur={() =>
                          headerFilters.recentComments &&
                          applyBulkEdit(
                            "recentComments",
                            headerFilters.recentComments,
                          )
                        }
                        inputProps={{ maxLength: 2000 }}
                        sx={{
                          "& .MuiInputBase-input": fontStyles,
                          "& .MuiInputLabel-root": fontStyles,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          ...fontStyles,
                          fontSize: "0.65rem",
                          position: "absolute",
                          right: 8,
                          bottom: -18,
                          color: "#666",
                        }}
                      >
                        {headerFilters.recentComments.length}/2000
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>{renderTableBody()}</TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={tableData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                fontStyles,
              "& .MuiTablePagination-select": fontStyles,
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => handleEdit(menuRecord)}
            sx={{
              ...fontStyles,
              "&:hover": {
                color: fontStyles.color,
              },
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(menuRecord)}
            sx={{
              ...fontStyles,
              "&:hover": {
                color: fontStyles.color,
              },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={headerFontStyles}>
            {editMode
              ? "Edit Core Ratio Mapping"
              : "Add New Core Ratio Mapping"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mt: 2,
              }}
            >
              <Autocomplete
                options={programs}
                getOptionLabel={(option) =>
                  option ? `${option.name} (${option.code})` : ""
                }
                value={formData.program}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    program: value,
                    programCode: value?.code || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Program *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Program Code"
                value={formData.programCode}
                disabled
                fullWidth
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name || ""}
                value={formData.country}
                onChange={(e, value) =>
                  setFormData({
                    ...formData,
                    country: value,
                    countryCode: value?.code || "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Country Code"
                value={formData.countryCode}
                disabled
                fullWidth
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(value) =>
                  setFormData({ ...formData, startDate: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(value) =>
                  setFormData({ ...formData, endDate: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <Autocomplete
                options={coreRatioBuckets}
                getOptionLabel={(option) => option.name || ""}
                value={formData.coreRatioBucket}
                onChange={(e, value) =>
                  setFormData({ ...formData, coreRatioBucket: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Core Ratio Bucket *"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <TextField
                label="Core Ratio Target"
                type="number"
                value={formData.coreRatioTarget}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    coreRatioTarget: e.target.value,
                  })
                }
                fullWidth
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{
                  "& .MuiInputBase-input": fontStyles,
                  "& .MuiInputLabel-root": fontStyles,
                }}
              />

              <Autocomplete
                options={billabilityTypes}
                getOptionLabel={(option) => option.name || ""}
                value={formData.billability}
                onChange={(e, value) =>
                  setFormData({ ...formData, billability: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Billability"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <Autocomplete
                options={basisTypes}
                getOptionLabel={(option) => option.name || ""}
                value={formData.basis}
                onChange={(e, value) =>
                  setFormData({ ...formData, basis: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Basis"
                    sx={{
                      "& .MuiInputBase-input": fontStyles,
                      "& .MuiInputLabel-root": fontStyles,
                    }}
                  />
                )}
              />

              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  label="Documentation"
                  multiline
                  rows={3}
                  value={formData.documentation}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFormData({
                      ...formData,
                      documentation: newValue,
                    });
                    setValidationErrors({
                      ...validationErrors,
                      documentation: validateDocumentation(newValue),
                    });
                  }}
                  fullWidth
                  error={!!validationErrors.documentation}
                  helperText={
                    validationErrors.documentation ||
                    `${formData.documentation.length}/300 characters`
                  }
                  inputProps={{ maxLength: 300 }}
                  sx={{
                    "& .MuiInputBase-input": fontStyles,
                    "& .MuiInputLabel-root": fontStyles,
                    "& .MuiFormHelperText-root": fontStyles,
                  }}
                />
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  label="Recent Comments"
                  multiline
                  rows={3}
                  value={formData.recentComments}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFormData({
                      ...formData,
                      recentComments: newValue,
                    });
                    setValidationErrors({
                      ...validationErrors,
                      recentComments: validateRecentComments(newValue),
                    });
                  }}
                  fullWidth
                  error={!!validationErrors.recentComments}
                  helperText={
                    validationErrors.recentComments ||
                    `${formData.recentComments.length}/2000 characters`
                  }
                  inputProps={{ maxLength: 2000 }}
                  sx={{
                    "& .MuiInputBase-input": fontStyles,
                    "& .MuiInputLabel-root": fontStyles,
                    "& .MuiFormHelperText-root": fontStyles,
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              color="primary"
              sx={{ ...fontStyles, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              sx={{
                ...headerFontStyles,
                textTransform: "none",
                color: "#FFFFFF",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleConfirmDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={headerFontStyles}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, ...fontStyles }}>
              {typeof confirmDialog.message === "string" ? (
                <Typography>{confirmDialog.message}</Typography>
              ) : (
                confirmDialog.message
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmDialogClose}
              variant="outlined"
              color="primary"
              sx={{ ...fontStyles, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDialogAccept}
              variant="contained"
              color={confirmDialog.confirmColor}
              sx={{
                ...headerFontStyles,
                textTransform: "none",
                color: "#FFFFFF",
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      </LocalizationProvider>
    </>
  );
};

export default CoreRatioTables;
